import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tsGetTool, tsUpdateTool, tsListMachines, tsIssueTool, tsReturnTool } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save, Lock, Zap, Edit3, History, MapPin, Wrench, AlertTriangle } from 'lucide-react';

const STATUS_COLORS = {
  in_store: '#0e7a45', issued: '#d97706', in_tool_room: '#1a56db',
  at_external: '#7c3aed', pending_scrap: '#dc2626',
  scrapped: '#6b7280', converted: '#6b7280',
};

export default function ToolDetailPage() {
  const { toolId } = useParams();
  const navigate = useNavigate();
  const [tool, setTool] = useState(null);
  const [machines, setMachines] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, m] = await Promise.all([tsGetTool(toolId), tsListMachines()]);
      setTool(t.data);
      setMachines(m.data || []);
      setForm({});
    } catch (err) {
      toast.error('Tool not found');
      navigate('/tool-store');
    }
  }, [toolId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!tool) return <div className="text-center text-gray-400 py-20 text-sm">Loading…</div>;

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.current_stock !== undefined) payload.current_stock = parseInt(payload.current_stock, 10);
      if (payload.min_level !== undefined) payload.min_level = parseInt(payload.min_level, 10);
      if (payload.reference_cost !== undefined) payload.reference_cost = parseFloat(payload.reference_cost);
      await tsUpdateTool(tool.id, payload);
      toast.success('Saved');
      setEditMode(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setSaving(false);
  };

  const completeQuickReg = async () => {
    try {
      await tsUpdateTool(tool.id, { complete_quick_register: true });
      toast.success('Quick Register marked complete');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const val = (k, def = '—') => (editMode ? (form[k] !== undefined ? form[k] : tool[k]) : (tool[k] || def));
  const setV = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const isQuickPending = tool.quick_register && !tool.quick_register_completed;

  return (
    <div className="space-y-3" data-testid="tool-detail-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate('/tool-store')} className="h-8 text-xs" data-testid="back-btn">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <div className="text-2xl font-mono font-bold text-[#0d1f6e]" data-testid="tl-number">{tool.tl_number}</div>
        <Lock size={12} className="text-gray-400" />
        <Badge variant="outline" className="text-[10px]" style={{ borderColor: STATUS_COLORS[tool.status], color: STATUS_COLORS[tool.status] }}>
          {tool.status.replace('_', ' ')}
        </Badge>
        {tool.is_critical && <Badge variant="outline" className="text-[10px] border-red-400 text-red-600">Critical</Badge>}
        <div className="flex-1" />
        {!editMode ? (
          <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className="h-8 gap-1" data-testid="edit-tool-btn">
            <Edit3 size={12} /> Edit
          </Button>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => { setEditMode(false); setForm({}); }} className="h-8">Cancel</Button>
            <Button size="sm" onClick={saveEdit} disabled={saving} className="h-8 gap-1" style={{ backgroundColor: '#1a2fce' }} data-testid="save-edit">
              <Save size={12} /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        )}
      </div>

      {isQuickPending && (
        <Card className="border-0 shadow-sm border-l-4 border-l-orange-500">
          <CardContent className="p-3 flex items-center gap-2">
            <Zap size={14} className="text-orange-600" />
            <div className="text-xs flex-1">
              <div className="font-semibold text-orange-800">Quick Register — incomplete</div>
              <div className="text-orange-700">
                Deadline: {tool.quick_register_deadline ? new Date(tool.quick_register_deadline).toLocaleString() : '—'}. Complete full details, then mark done.
              </div>
            </div>
            <Button size="sm" onClick={completeQuickReg} className="h-8 text-[11px] bg-orange-600 hover:bg-orange-700" data-testid="complete-quick-reg">
              Mark Complete
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Left 2/3: Identity + material + stock */}
        <div className="lg:col-span-2 space-y-3">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Identity & Physical</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <Field label="TL Number" locked value={tool.tl_number} />
              <Field label="Functional ID" mono value={val('functional_id')} onChange={editMode ? (v) => setV('functional_id', v) : null} />
              <Field label="Existing Engraved No." value={val('existing_engraved_number')} onChange={editMode ? (v) => setV('existing_engraved_number', v) : null} />
              <Field label="Description" value={val('description')} onChange={editMode ? (v) => setV('description', v) : null} />
              <Field label="Size" value={val('size')} onChange={editMode ? (v) => setV('size', v) : null} />
              <Field label="Category" value={tool.category || '—'} />
              <Field label="Machines" mono value={(tool.machine_compatibility || []).join(', ') || '—'} />
              <Field label="Station" value={val('station_number')} onChange={editMode ? (v) => setV('station_number', v) : null} />
              <Field label="Drawing Number" value={val('drawing_number')} onChange={editMode ? (v) => setV('drawing_number', v) : null} />
              <Field label="Item Type" value={tool.item_type} />
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Material & Vendor</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <Field label="Material" value={val('material_type')} onChange={editMode ? (v) => setV('material_type', v) : null} />
              <Field label="Carbide Grade" value={val('carbide_grade')} onChange={editMode ? (v) => setV('carbide_grade', v) : null} />
              <Field label="Steel Grade" value={val('steel_grade')} onChange={editMode ? (v) => setV('steel_grade', v) : null} />
              <Field label="Vendor" value={val('vendor')} onChange={editMode ? (v) => setV('vendor', v) : null} />
              <Field label="Country" value={val('country_of_origin')} onChange={editMode ? (v) => setV('country_of_origin', v) : null} />
              <Field label="PO Ref" value={val('po_reference')} onChange={editMode ? (v) => setV('po_reference', v) : null} />
              <Field label="Material Cert No." mono value={val('material_cert_number')} onChange={editMode ? (v) => setV('material_cert_number', v) : null} />
              <Field label="Heat Number" mono value={val('heat_number')} onChange={editMode ? (v) => setV('heat_number', v) : null} />
              <Field label="Reference Cost (₹)" value={tool.reference_cost != null ? `₹${tool.reference_cost.toLocaleString()}` : '—'} onChange={editMode ? (v) => setV('reference_cost', v) : null} />
              <Field label="Date of Receipt" value={val('date_of_receipt')} />
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Life Tracking</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
              <Field label="Original Length" value={tool.original_length_mm ? `${tool.original_length_mm} mm` : '—'} />
              <Field label="Current Length" value={tool.current_length_mm ? `${tool.current_length_mm} mm` : '—'} />
              <Field label="Min Serviceable" value={tool.minimum_serviceable_length_mm ? `${tool.minimum_serviceable_length_mm} mm` : '—'} />
              <Field label="Regrind Count" value={`${tool.regrind_count || 0}${tool.max_regrind_limit ? '/' + tool.max_regrind_limit : ''}`} />
              <Field label="Hits Range" value={val('hits_range')} />
              <Field label="Expected Hits/Cycle" value={tool.expected_hits_per_cycle || '—'} />
            </CardContent>
          </Card>
        </div>

        {/* Right 1/3: stock/location + txns */}
        <div className="space-y-3">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><MapPin size={14} /> Stock & Location</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-xs">
              <Field label="Current Stock" value={editMode ? (form.current_stock ?? tool.current_stock) : tool.current_stock} onChange={editMode ? (v) => setV('current_stock', v) : null} />
              <Field label="Min Level" value={editMode ? (form.min_level ?? tool.min_level ?? '') : (tool.min_level ?? '—')} onChange={editMode ? (v) => setV('min_level', v) : null} />
              <Field label="Bin Location" mono value={val('bin_location')} onChange={editMode ? (v) => setV('bin_location', v) : null} />
              {tool.min_level && tool.current_stock < tool.min_level && (
                <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
                  <AlertTriangle size={12} /> Below reorder level
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><History size={14} /> Recent Transactions</CardTitle></CardHeader>
            <CardContent className="space-y-1.5" data-testid="tool-txns">
              {(tool.transactions || []).length === 0 ? (
                <div className="text-xs text-gray-400 italic">No transactions yet</div>
              ) : (tool.transactions || []).slice(0, 15).map((tx) => (
                <div key={tx.id} className="text-[11px] border-l-2 pl-2" style={{ borderColor: tx.type === 'issue' ? '#d97706' : '#0e7a45' }}>
                  <div className="flex items-center gap-1">
                    <span className={`font-semibold ${tx.type === 'issue' ? 'text-amber-700' : 'text-green-700'}`}>
                      {tx.type === 'issue' ? '↑ Issued' : '↓ Returned'}
                    </span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-500">{tx.machine || tx.condition || ''}</span>
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {tx.operator || tx.hits_used_range || ''} · {new Date(tx.timestamp).toLocaleString()}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, mono, locked }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-gray-500 mb-0.5 flex items-center gap-1">
        {label}
        {locked && <Lock size={9} />}
      </div>
      {onChange && !locked ? (
        <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={`h-8 text-xs ${mono ? 'font-mono' : ''}`} />
      ) : (
        <div className={`text-xs ${mono ? 'font-mono' : ''} ${!value || value === '—' ? 'text-gray-400' : 'text-gray-800'}`}>{value ?? '—'}</div>
      )}
    </div>
  );
}
